/** Copyright (c) 2021 T. Rowe Price. All rights reserved. */

import { isEmpty } from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { yyyymmdd2mmmddyyyy } from 'services/Dates';
import { AccountNameRenderer } from 'services/TableUtils';
import {
  autoGroupColumnDef,
  autoGroupColumnDefMobile,
  ColumnDefFactory,
  defaultColDef
} from 'components/layouts/Performance/ColumnDefs';
import {
  getAccounts,
  getBrokerageAccountsFromSweepMap
} from 'state/account/AccountSelectors';

import { isDesktopLayout } from 'state/view/ViewSelectors';
import {
  getOpenRateOfReturnSummary,
  getClosedRateOfReturnSummary
} from 'state/account/AccountPerformanceSelectors';
import {
  getAggregatedProductPerformanceData,
  getModelBenchmark,
  getProductPerformanceData
} from 'state/performance/ProductPerformanceSelectors';
import {
  isActiveAdvisory,
  isActivePlus,
  isMutualFund,
  isCollegeSavings
} from 'services/Products';
import {
  getAsOfDateForRateOfReturn,
  getInceptionDateForRateOfReturn,
  getRateOfReturnFilter,
  getShowMultipleActivePlusForRateOfReturn
} from 'state/performance/RateOfReturnFilterSelectors';
import {
  has529Only,
  hasAlaska529Only,
  shouldUseAggregatedPerformance
} from 'state/performance/PerformancePageSelectors';

const RateOfReturnDataProvider = (WrappedComponent, closed = false) => {
  class RateOfReturnData extends Component {
    frameworkComponents = {
      accountNameRenderer: AccountNameRenderer
    };

    generateColumnDefs = (
      rateOfReturnSummary,
      retirementOrder = false,
      has529Only = false,
      hasAlaska529Only = false,
      showMultipleActivePlus
    ) => {
      if (isEmpty(rateOfReturnSummary)) {
        return [];
      }

      const columnDefFactory = new ColumnDefFactory(
        retirementOrder,
        has529Only,
        hasAlaska529Only,
        showMultipleActivePlus,
        closed
      );
      const columnDefOrder = [
        'personalInvestment',
        'productClass',
        'rasBenchmarkRow',
        'retirementAccount',
        'productType',
        'typeAndOwners',
        'accountNameAndNumber',
        'monthToDateRateOfReturn',
        'quarterToDateRateOfReturn',
        'lastQuarterReturn',
        'ytdReturn',
        'oneYearReturn',
        'threeYearReturn',
        'fiveYearReturn',
        'tenYearReturn',
        'inceptionReturn',
        'inceptionDate'
      ];

      if (
        !rateOfReturnSummary.some(
          (account) =>
            isActiveAdvisory(account.productType) ||
            isActivePlus(account.productType)
        )
      ) {
        return columnDefOrder
          .filter(
            (field) =>
              field !== 'monthToDateRateOfReturn' &&
              field !== 'quarterToDateRateOfReturn'
          )
          .map((field) => columnDefFactory.createColumnDef(field));
      } else if (
        !rateOfReturnSummary.some(
          (account) =>
            isMutualFund(account.productType) ||
            isCollegeSavings(account.productType)
        )
      ) {
        return columnDefOrder
          .filter((field) => field !== 'lastQuarterReturn')
          .map((field) => columnDefFactory.createColumnDef(field));
      }

      return columnDefOrder.map((field) =>
        columnDefFactory.createColumnDef(field)
      );
    };

    render() {
      const {
        fromDate,
        toDate,
        rateOfReturnSummary,
        maxInitialRowsToShow,
        performanceFilter,
        has529Only,
        hasAlaska529Only,
        shouldUseAggregatedPerformance,
        rasBenchmark,
        productPerformance,
        aggregatedProductPerformance,
        autoGroupColumnDef,
        isDesktopLayout,
        showMultipleActivePlus,
        themeName,
        sweepMap
      } = this.props;

      const data = {
        columnDefs: this.generateColumnDefs(
          rateOfReturnSummary,
          false,
          has529Only,
          hasAlaska529Only,
          showMultipleActivePlus
        ),
        columnDefsRetirementOrder: this.generateColumnDefs(
          rateOfReturnSummary,
          true,
          has529Only,
          hasAlaska529Only,
          showMultipleActivePlus
        ),
        applyColumnDefOrder: true,
        defaultColDef
      };

      return (
        <WrappedComponent
          fromDate={fromDate}
          toDate={toDate}
          rateOfReturnSummary={rateOfReturnSummary}
          maxInitialRowsToShow={maxInitialRowsToShow}
          performanceFilter={performanceFilter}
          frameworkComponents={this.frameworkComponents}
          rasBenchmark={rasBenchmark}
          productPerformance={productPerformance}
          aggregatedProductPerformance={aggregatedProductPerformance}
          autoGroupColumnDef={autoGroupColumnDef}
          isDesktopLayout={isDesktopLayout}
          showMultipleActivePlus={showMultipleActivePlus}
          has529Only={has529Only}
          shouldUseAggregatedPerformance={shouldUseAggregatedPerformance}
          themeName={themeName}
          sweepMap={sweepMap}
          {...data}
        />
      );
    }
  }

  const mapStateToProps = (state) => {
    const { view } = state;
    const rateOfReturnSummary = closed
      ? getClosedRateOfReturnSummary(state)
      : getOpenRateOfReturnSummary(state);
    const desktopLayout = isDesktopLayout(state);

    return {
      rateOfReturnSummary,
      themeName: view.themeName,
      accounts: getAccounts(state),
      isDesktopLayout: desktopLayout,
      fromDate: yyyymmdd2mmmddyyyy(getInceptionDateForRateOfReturn(state)),
      toDate: yyyymmdd2mmmddyyyy(getAsOfDateForRateOfReturn(state)),
      maxInitialRowsToShow: view.maxInitialRowsToShow,
      performanceFilter: getRateOfReturnFilter(state),
      has529Only: has529Only(state),
      hasAlaska529Only: hasAlaska529Only(state),
      shouldUseAggregatedPerformance: shouldUseAggregatedPerformance(state),
      rasBenchmark: getModelBenchmark(state),
      productPerformance: getProductPerformanceData(state),
      aggregatedProductPerformance: getAggregatedProductPerformanceData(state),
      autoGroupColumnDef: desktopLayout
        ? autoGroupColumnDef
        : autoGroupColumnDefMobile,
      showMultipleActivePlus: getShowMultipleActivePlusForRateOfReturn(state),
      sweepMap: getBrokerageAccountsFromSweepMap(state)
    };
  };

  return connect(mapStateToProps)(RateOfReturnData);
};

export default RateOfReturnDataProvider;
